import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, UsersIcon, CheckCircleIcon, XCircleIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon, MonitorIcon, WifiIcon, CoffeeIcon, PlusIcon } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  location: string;
  capacity: number;
  floor: string;
  features: string[];
  color: string;
  bookings: Booking[];
}
interface Booking {
  id: string;
  title: string;
  organizer: string;
  avatar: string;
  avatarColor: string;
  start: string;
  end: string;
  date: string;
  attendees: number;
}
const today = new Date();
const formatDate = (d: Date) => d.toISOString().split('T')[0];
const rooms: Room[] = [{
  id: 'falcon',
  name: 'Falcon Room',
  location: 'HQ — Floor 2',
  capacity: 12,
  floor: '2nd Floor',
  color: '#2B8DC4',
  features: ['Video Conferencing', 'Whiteboard', 'WiFi', 'Coffee Station'],
  bookings: [{
    id: 'b1',
    title: 'Q1 Pipeline Review',
    organizer: 'Sarah Kim',
    avatar: 'SK',
    avatarColor: '#2B8DC4',
    start: '09:00',
    end: '10:00',
    date: formatDate(today),
    attendees: 8
  }, {
    id: 'b2',
    title: 'Compliance Training',
    organizer: 'Lisa Martinez',
    avatar: 'LM',
    avatarColor: '#1A2B3C',
    start: '13:00',
    end: '14:30',
    date: formatDate(today),
    attendees: 10
  }, {
    id: 'b3',
    title: 'New Hire Onboarding',
    organizer: 'HR Team',
    avatar: 'HR',
    avatarColor: '#1E6FA0',
    start: '15:00',
    end: '16:00',
    date: formatDate(today),
    attendees: 5
  }]
}, {
  id: 'swift',
  name: 'Swift Suite',
  location: 'HQ — Floor 3',
  capacity: 20,
  floor: '3rd Floor',
  color: '#1A2B3C',
  features: ['Video Conferencing', 'Projector', 'Whiteboard', 'WiFi', 'Coffee Station'],
  bookings: [{
    id: 'b4',
    title: 'All-Hands Meeting',
    organizer: 'Jennifer Walsh',
    avatar: 'JW',
    avatarColor: '#1A2B3C',
    start: '10:00',
    end: '11:30',
    date: formatDate(today),
    attendees: 18
  }, {
    id: 'b5',
    title: 'Leadership Sync',
    organizer: 'Robert Chen',
    avatar: 'RC',
    avatarColor: '#2B8DC4',
    start: '14:00',
    end: '15:00',
    date: formatDate(today),
    attendees: 6
  }]
}, {
  id: 'horizon',
  name: 'Horizon Room',
  location: 'HQ — Floor 1',
  capacity: 6,
  floor: '1st Floor',
  color: '#1E6FA0',
  features: ['TV Screen', 'Whiteboard', 'WiFi'],
  bookings: [{
    id: 'b6',
    title: '1:1 with Marcus',
    organizer: 'Carlos Mendez',
    avatar: 'CM',
    avatarColor: '#1E6FA0',
    start: '11:00',
    end: '11:30',
    date: formatDate(today),
    attendees: 2
  }]
}, {
  id: 'apex',
  name: 'Apex Boardroom',
  location: 'HQ — Floor 4',
  capacity: 16,
  floor: '4th Floor',
  color: '#243447',
  features: ['Video Conferencing', 'Dual Projectors', 'Whiteboard', 'WiFi', 'Coffee Station', 'Catering Available'],
  bookings: []
}];
const timeSlots = ['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00'];
const featureIcons: Record<string, React.ReactNode> = {
  'Video Conferencing': <MonitorIcon size={13} data-id="element-435" />,
  WiFi: <WifiIcon size={13} data-id="element-436" />,
  'Coffee Station': <CoffeeIcon size={13} data-id="element-437" />,
  Whiteboard: <span style={{
    fontSize: 12
  }} data-id="element-438">
      ✏️
    </span>,
  Projector: <span style={{
    fontSize: 12
  }} data-id="element-439">
      📽️
    </span>,
  'TV Screen': <span style={{
    fontSize: 12
  }} data-id="element-440">
      📺
    </span>,
  'Dual Projectors': <span style={{
    fontSize: 12
  }} data-id="element-441">
      📽️
    </span>,
  'Catering Available': <span style={{
    fontSize: 12
  }} data-id="element-442">
      🍽️
    </span>
};
function isSlotBooked(room: Room, date: string, slot: string): Booking | null {
  return room.bookings.find(b => {
    if (b.date !== date) return false;
    return slot >= b.start && slot < b.end;
  }) || null;
}
function getWeekDays(baseDate: Date): Date[] {
  const days: Date[] = [];
  const day = baseDate.getDay();
  const monday = new Date(baseDate);
  monday.setDate(baseDate.getDate() - (day === 0 ? 6 : day - 1));
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}
const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function MeetingRoom() {
  const [selectedRoom, setSelectedRoom] = useState<Room>(rooms[0]);
  const [selectedDate, setSelectedDate] = useState<string>(formatDate(today));
  const [weekOffset, setWeekOffset] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [bookingForm, setBookingForm] = useState({
    title: '',
    attendees: '2',
    endSlot: '',
    notes: ''
  });
  const [confirmedBookings, setConfirmedBookings] = useState<Booking[]>([]);
  const [successMsg, setSuccessMsg] = useState('');
  const baseDate = new Date(today);
  baseDate.setDate(today.getDate() + weekOffset * 7);
  const weekDays = getWeekDays(baseDate);
  const allBookings = [...selectedRoom.bookings, ...confirmedBookings.filter(b => b.id.startsWith(selectedRoom.id))];
  const handleSlotClick = (slot: string) => {
    const booking = isSlotBooked({
      ...selectedRoom,
      bookings: allBookings
    }, selectedDate, slot);
    if (booking) return;
    setSelectedSlot(slot);
    const idx = timeSlots.indexOf(slot);
    setBookingForm(f => ({
      ...f,
      endSlot: timeSlots[Math.min(idx + 2, timeSlots.length - 1)],
      title: '',
      notes: '',
      attendees: '2'
    }));
    setShowBookingModal(true);
  };
  const handleConfirmBooking = () => {
    if (!bookingForm.title.trim()) return;
    const newBooking: Booking = {
      id: `${selectedRoom.id}-${Date.now()}`,
      title: bookingForm.title,
      organizer: 'Jamie Davis',
      avatar: 'JD',
      avatarColor: '#2B8DC4',
      start: selectedSlot,
      end: bookingForm.endSlot,
      date: selectedDate,
      attendees: parseInt(bookingForm.attendees) || 2
    };
    setConfirmedBookings(prev => [...prev, newBooking]);
    setShowBookingModal(false);
    setSuccessMsg(`✅ "${bookingForm.title}" booked in ${selectedRoom.name} at ${selectedSlot}`);
    setTimeout(() => setSuccessMsg(''), 4000);
  };
  const selectedDateObj = new Date(selectedDate + 'T12:00:00');
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-443">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-444">
        <div data-id="element-445">
          <h2 className="text-2xl font-bold" style={{
          color: '#1A2B3C',
          fontStyle: 'italic',
          letterSpacing: '-0.03em'
        }} data-id="element-446">
            Meeting Room Booking
          </h2>
          <p className="text-sm mt-1" style={{
          color: '#5A6A8A'
        }} data-id="element-447">
            Reserve a space for your next meeting, training, or team sync
          </p>
        </div>
        <button onClick={() => {
        setSelectedSlot(timeSlots[2]);
        setBookingForm(f => ({
          ...f,
          endSlot: timeSlots[4],
          title: '',
          notes: '',
          attendees: '2'
        }));
        setShowBookingModal(true);
      }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90" style={{
        background: '#2B8DC4'
      }} data-id="element-448">
          <PlusIcon size={16} data-id="element-449" /> Book a Room
        </button>
      </div>

      {/* Success Toast */}
      {successMsg && <div className="rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2" style={{
      background: '#EBF5FB',
      border: '1px solid #2B8DC4',
      color: '#1E6FA0'
    }} data-id="element-450">
          {successMsg}
        </div>}

      {/* Room Selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-451">
        {rooms.map(room => {
        const isSelected = selectedRoom.id === room.id;
        const todayBookings = [...room.bookings, ...confirmedBookings.filter(b => b.id.startsWith(room.id))].filter(b => b.date === selectedDate);
        return <button key={room.id} onClick={() => setSelectedRoom(room)} className="text-left rounded-xl p-4 border-2 transition-all hover:shadow-md" style={{
          background: isSelected ? `${room.color}08` : '#FFFFFF',
          borderColor: isSelected ? room.color : '#E4EAF6',
          boxShadow: isSelected ? `0 0 0 3px ${room.color}20` : undefined,
          borderTopWidth: isSelected ? 3 : 1,
          borderTopColor: isSelected ? room.color : '#E4EAF6'
        }} data-id="element-452">
              <div className="flex items-start justify-between mb-2" data-id="element-453">
                <div className="rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{
              width: 36,
              height: 36,
              background: room.color
            }} data-id="element-454">
                  {room.name.charAt(0)}
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{
              background: todayBookings.length > 0 ? '#EBF5FB' : '#F0FDF4',
              color: todayBookings.length > 0 ? '#2B8DC4' : '#16A34A'
            }} data-id="element-455">
                  {todayBookings.length > 0 ? `${todayBookings.length} booked` : 'Available'}
                </span>
              </div>
              <div className="font-bold text-sm mb-0.5" style={{
            color: '#1A2B3C'
          }} data-id="element-456">
                {room.name}
              </div>
              <div className="text-xs flex items-center gap-1" style={{
            color: '#5A6A8A'
          }} data-id="element-457">
                <MapPinIcon size={11} data-id="element-458" /> {room.floor}
              </div>
              <div className="text-xs flex items-center gap-1 mt-0.5" style={{
            color: '#5A6A8A'
          }} data-id="element-459">
                <UsersIcon size={11} data-id="element-460" /> Up to {room.capacity} people
              </div>
            </button>;
      })}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-461">
        {/* Calendar + Timeline */}
        <div className="lg:col-span-2 space-y-4" data-id="element-462">
          {/* Week Navigator */}
          <div className="bg-white rounded-xl p-4" style={{
          border: '1px solid #E4EAF6',
          borderTop: '3px solid #2B8DC4',
          boxShadow: '0 2px 8px rgba(13,27,42,0.06)'
        }} data-id="element-463">
            <div className="flex items-center justify-between mb-4" data-id="element-464">
              <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-465">
                <ChevronLeftIcon size={18} color="#5A6A8A" data-id="element-466" />
              </button>
              <span className="font-bold text-sm" style={{
              color: '#1A2B3C',
              fontStyle: 'italic'
            }} data-id="element-467">
                {monthNames[weekDays[0].getMonth()]} {weekDays[0].getDate()} –{' '}
                {monthNames[weekDays[6].getMonth()]} {weekDays[6].getDate()},{' '}
                {weekDays[6].getFullYear()}
              </span>
              <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-468">
                <ChevronRightIcon size={18} color="#5A6A8A" data-id="element-469" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1" data-id="element-470">
              {weekDays.map((d, i) => {
              const dateStr = formatDate(d);
              const isToday = dateStr === formatDate(today);
              const isSelected = dateStr === selectedDate;
              const hasBookings = [...selectedRoom.bookings, ...confirmedBookings.filter(b => b.id.startsWith(selectedRoom.id))].some(b => b.date === dateStr);
              return <button key={i} onClick={() => setSelectedDate(dateStr)} className="flex flex-col items-center py-2 px-1 rounded-xl transition-all" style={{
                background: isSelected ? '#2B8DC4' : isToday ? '#EBF5FB' : 'transparent',
                color: isSelected ? '#FFFFFF' : isToday ? '#2B8DC4' : '#5A6A8A'
              }} data-id="element-471">
                    <span className="text-xs font-medium" data-id="element-472">{dayLabels[i]}</span>
                    <span className="text-base font-bold mt-0.5" data-id="element-473">
                      {d.getDate()}
                    </span>
                    {hasBookings && <span className="rounded-full mt-1" style={{
                  width: 5,
                  height: 5,
                  background: isSelected ? 'rgba(255,255,255,0.7)' : selectedRoom.color
                }} data-id="element-474" />}
                  </button>;
            })}
            </div>
          </div>

          {/* Time Grid */}
          <div className="bg-white rounded-xl overflow-hidden" style={{
          border: '1px solid #E4EAF6',
          borderTop: '3px solid #1A2B3C',
          boxShadow: '0 2px 8px rgba(13,27,42,0.06)'
        }} data-id="element-475">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{
            borderColor: '#E4EAF6'
          }} data-id="element-476">
              <div data-id="element-477">
                <span className="font-bold text-sm" style={{
                color: '#1A2B3C',
                fontStyle: 'italic'
              }} data-id="element-478">
                  {selectedRoom.name}
                </span>
                <span className="text-xs ml-2" style={{
                color: '#5A6A8A'
              }} data-id="element-479">
                  {selectedDateObj.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs" style={{
              color: '#5A6A8A'
            }} data-id="element-480">
                <span className="flex items-center gap-1.5" data-id="element-481">
                  <span className="inline-block rounded" style={{
                  width: 12,
                  height: 12,
                  background: '#E4EAF6'
                }} data-id="element-482" />{' '}
                  Available
                </span>
                <span className="flex items-center gap-1.5" data-id="element-483">
                  <span className="inline-block rounded" style={{
                  width: 12,
                  height: 12,
                  background: selectedRoom.color
                }} data-id="element-484" />{' '}
                  Booked
                </span>
              </div>
            </div>
            <div className="p-4 overflow-y-auto" style={{
            maxHeight: 420
          }} data-id="element-485">
              <div className="space-y-1" data-id="element-486">
                {timeSlots.map(slot => {
                const booking = isSlotBooked({
                  ...selectedRoom,
                  bookings: allBookings
                }, selectedDate, slot);
                const isStart = booking && booking.start === slot;
                const isMyBooking = booking && booking.avatar === 'JD';
                return <div key={slot} className="flex items-stretch gap-3" data-id="element-487">
                      <div className="text-xs font-medium flex-shrink-0 flex items-center" style={{
                    width: 44,
                    color: '#A0AECB'
                  }} data-id="element-488">
                        {slot}
                      </div>
                      <button onClick={() => handleSlotClick(slot)} disabled={!!booking} className="flex-1 rounded-lg px-3 py-2 text-left text-xs transition-all min-h-[36px]" style={{
                    background: booking ? isMyBooking ? `${selectedRoom.color}25` : `${selectedRoom.color}15` : '#F4F8FC',
                    border: `1px solid ${booking ? selectedRoom.color + '40' : '#E4EAF6'}`,
                    cursor: booking ? 'default' : 'pointer',
                    color: booking ? selectedRoom.color : '#A0AECB'
                  }} onMouseEnter={e => {
                    if (!booking) (e.currentTarget as HTMLButtonElement).style.background = `${selectedRoom.color}10`;
                  }} onMouseLeave={e => {
                    if (!booking) (e.currentTarget as HTMLButtonElement).style.background = '#F4F8FC';
                  }} data-id="element-489">
                        {isStart && booking ? <div className="flex items-center gap-2" data-id="element-490">
                            <div className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" style={{
                        width: 20,
                        height: 20,
                        background: booking.avatarColor,
                        fontSize: 9
                      }} data-id="element-491">
                              {booking.avatar}
                            </div>
                            <div data-id="element-492">
                              <div className="font-semibold" style={{
                          color: selectedRoom.color
                        }} data-id="element-493">
                                {booking.title}
                              </div>
                              <div style={{
                          color: '#A0AECB'
                        }} data-id="element-494">
                                {booking.organizer} · {booking.start}–
                                {booking.end} · {booking.attendees} attendees
                              </div>
                            </div>
                          </div> : booking ? <span style={{
                      color: `${selectedRoom.color}60`
                    }} data-id="element-495">
                            —
                          </span> : <span className="hover:font-medium transition-all" data-id="element-496">
                            + Click to book
                          </span>}
                      </button>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="space-y-4" data-id="element-497">
          {/* Room Details */}
          <div className="rounded-xl p-5 text-white" style={{
          background: `linear-gradient(135deg, ${selectedRoom.color} 0%, ${selectedRoom.color}CC 100%)`
        }} data-id="element-498">
            <h3 className="font-bold text-lg mb-1" style={{
            fontStyle: 'italic',
            letterSpacing: '-0.02em'
          }} data-id="element-499">
              {selectedRoom.name}
            </h3>
            <div className="flex items-center gap-1.5 text-sm opacity-80 mb-3" data-id="element-500">
              <MapPinIcon size={14} data-id="element-501" /> {selectedRoom.location}
            </div>
            <div className="flex items-center gap-1.5 text-sm opacity-80 mb-4" data-id="element-502">
              <UsersIcon size={14} data-id="element-503" /> Capacity: {selectedRoom.capacity} people
            </div>
            <div className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-2" data-id="element-504">
              Amenities
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-505">
              {selectedRoom.features.map(f => <span key={f} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium" style={{
              background: 'rgba(255,255,255,0.2)'
            }} data-id="element-506">
                  {featureIcons[f]} {f}
                </span>)}
            </div>
          </div>

          {/* Today's Schedule */}
          <div className="bg-white rounded-xl p-5" style={{
          border: '1px solid #E4EAF6',
          borderTop: '3px solid #2B8DC4',
          boxShadow: '0 2px 8px rgba(13,27,42,0.06)'
        }} data-id="element-507">
            <h4 className="font-bold text-sm mb-3 flex items-center gap-2" style={{
            color: '#1A2B3C',
            fontStyle: 'italic'
          }} data-id="element-508">
              <CalendarIcon size={16} color="#2B8DC4" data-id="element-509" />
              {selectedDateObj.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })}{' '}
              Schedule
            </h4>
            {allBookings.filter(b => b.date === selectedDate).length === 0 ? <div className="text-center py-6" data-id="element-510">
                <CheckCircleIcon size={32} color="#2B8DC4" className="mx-auto mb-2" data-id="element-511" />
                <p className="text-sm font-medium" style={{
              color: '#2B8DC4'
            }} data-id="element-512">
                  All clear!
                </p>
                <p className="text-xs mt-1" style={{
              color: '#A0AECB'
            }} data-id="element-513">
                  No bookings for this day
                </p>
              </div> : <div className="space-y-3" data-id="element-514">
                {allBookings.filter(b => b.date === selectedDate).sort((a, b) => a.start.localeCompare(b.start)).map(booking => <div key={booking.id} className="flex items-start gap-3" data-id="element-515">
                      <div className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" style={{
                width: 32,
                height: 32,
                background: booking.avatarColor,
                fontSize: 11
              }} data-id="element-516">
                        {booking.avatar}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-517">
                        <div className="font-semibold text-sm truncate" style={{
                  color: '#1A2B3C'
                }} data-id="element-518">
                          {booking.title}
                        </div>
                        <div className="text-xs" style={{
                  color: '#5A6A8A'
                }} data-id="element-519">
                          {booking.start} – {booking.end} · {booking.organizer}
                        </div>
                        <div className="text-xs flex items-center gap-1 mt-0.5" style={{
                  color: '#A0AECB'
                }} data-id="element-520">
                          <UsersIcon size={11} data-id="element-521" /> {booking.attendees} attendees
                        </div>
                      </div>
                    </div>)}
              </div>}
          </div>

          {/* All Rooms Quick Status */}
          <div className="bg-white rounded-xl p-5" style={{
          border: '1px solid #E4EAF6',
          borderTop: '3px solid #1A2B3C',
          boxShadow: '0 2px 8px rgba(13,27,42,0.06)'
        }} data-id="element-522">
            <h4 className="font-bold text-sm mb-3" style={{
            color: '#1A2B3C',
            fontStyle: 'italic'
          }} data-id="element-523">
              All Rooms Today
            </h4>
            <div className="space-y-2" data-id="element-524">
              {rooms.map(room => {
              const todayCount = [...room.bookings, ...confirmedBookings.filter(b => b.id.startsWith(room.id))].filter(b => b.date === selectedDate).length;
              const available = todayCount === 0;
              return <button key={room.id} onClick={() => setSelectedRoom(room)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left" data-id="element-525">
                    <div className="rounded-lg flex-shrink-0" style={{
                  width: 10,
                  height: 10,
                  background: room.color,
                  borderRadius: 3
                }} data-id="element-526" />
                    <span className="text-sm flex-1 font-medium" style={{
                  color: '#1A2B3C'
                }} data-id="element-527">
                      {room.name}
                    </span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{
                  background: available ? '#F0FDF4' : '#EBF5FB',
                  color: available ? '#16A34A' : '#2B8DC4'
                }} data-id="element-528">
                      {available ? 'Free' : `${todayCount} mtg`}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{
      background: 'rgba(13,27,42,0.5)',
      backdropFilter: 'blur(4px)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setShowBookingModal(false);
    }} data-id="element-529">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-530">
            {/* Modal Header */}
            <div className="px-6 py-5 text-white" style={{
          background: `linear-gradient(135deg, ${selectedRoom.color} 0%, ${selectedRoom.color}CC 100%)`
        }} data-id="element-531">
              <div className="flex items-center justify-between" data-id="element-532">
                <div data-id="element-533">
                  <h3 className="font-bold text-lg" style={{
                fontStyle: 'italic',
                letterSpacing: '-0.02em'
              }} data-id="element-534">
                    Book {selectedRoom.name}
                  </h3>
                  <p className="text-sm opacity-80 mt-0.5" data-id="element-535">
                    {selectedDateObj.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
                  </p>
                </div>
                <button onClick={() => setShowBookingModal(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-id="element-536">
                  <XCircleIcon size={22} color="white" data-id="element-537" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4" data-id="element-538">
              <div data-id="element-539">
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{
              color: '#5A6A8A'
            }} data-id="element-540">
                  Meeting Title *
                </label>
                <input type="text" placeholder="e.g. Team Sync, Client Review..." value={bookingForm.title} onChange={e => setBookingForm(f => ({
              ...f,
              title: e.target.value
            }))} className="w-full px-3 py-2.5 text-sm rounded-lg border outline-none transition-all" style={{
              borderColor: '#E4EAF6',
              color: '#1A2B3C'
            }} onFocus={e => {
              e.target.style.borderColor = selectedRoom.color;
              e.target.style.boxShadow = `0 0 0 3px ${selectedRoom.color}20`;
            }} onBlur={e => {
              e.target.style.borderColor = '#E4EAF6';
              e.target.style.boxShadow = 'none';
            }} autoFocus data-id="element-541" />
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-542">
                <div data-id="element-543">
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{
                color: '#5A6A8A'
              }} data-id="element-544">
                    Start Time
                  </label>
                  <select value={selectedSlot} onChange={e => setSelectedSlot(e.target.value)} className="w-full px-3 py-2.5 text-sm rounded-lg border outline-none" style={{
                borderColor: '#E4EAF6',
                color: '#1A2B3C'
              }} data-id="element-545">
                    {timeSlots.map(t => <option key={t} value={t} data-id="element-546">
                        {t}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-547">
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{
                color: '#5A6A8A'
              }} data-id="element-548">
                    End Time
                  </label>
                  <select value={bookingForm.endSlot} onChange={e => setBookingForm(f => ({
                ...f,
                endSlot: e.target.value
              }))} className="w-full px-3 py-2.5 text-sm rounded-lg border outline-none" style={{
                borderColor: '#E4EAF6',
                color: '#1A2B3C'
              }} data-id="element-549">
                    {timeSlots.filter(t => t > selectedSlot).map(t => <option key={t} value={t} data-id="element-550">
                          {t}
                        </option>)}
                  </select>
                </div>
              </div>
              <div data-id="element-551">
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{
              color: '#5A6A8A'
            }} data-id="element-552">
                  Number of Attendees
                </label>
                <div className="flex items-center gap-3" data-id="element-553">
                  <input type="range" min={1} max={selectedRoom.capacity} value={bookingForm.attendees} onChange={e => setBookingForm(f => ({
                ...f,
                attendees: e.target.value
              }))} className="flex-1" style={{
                accentColor: selectedRoom.color
              }} data-id="element-554" />
                  <span className="text-sm font-bold px-3 py-1 rounded-lg" style={{
                background: `${selectedRoom.color}15`,
                color: selectedRoom.color,
                minWidth: 48,
                textAlign: 'center'
              }} data-id="element-555">
                    {bookingForm.attendees}
                  </span>
                </div>
                <div className="text-xs mt-1" style={{
              color: '#A0AECB'
            }} data-id="element-556">
                  Max capacity: {selectedRoom.capacity}
                </div>
              </div>
              <div data-id="element-557">
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{
              color: '#5A6A8A'
            }} data-id="element-558">
                  Notes (optional)
                </label>
                <textarea placeholder="Any special requirements or agenda..." value={bookingForm.notes} onChange={e => setBookingForm(f => ({
              ...f,
              notes: e.target.value
            }))} rows={2} className="w-full px-3 py-2.5 text-sm rounded-lg border outline-none resize-none" style={{
              borderColor: '#E4EAF6',
              color: '#1A2B3C'
            }} data-id="element-559" />
              </div>
              <div className="rounded-lg p-3 flex items-center gap-3" style={{
            background: `${selectedRoom.color}08`,
            border: `1px solid ${selectedRoom.color}20`
          }} data-id="element-560">
                <MapPinIcon size={16} color={selectedRoom.color} data-id="element-561" />
                <div data-id="element-562">
                  <div className="text-xs font-semibold" style={{
                color: selectedRoom.color
              }} data-id="element-563">
                    {selectedRoom.name} · {selectedRoom.location}
                  </div>
                  <div className="text-xs" style={{
                color: '#5A6A8A'
              }} data-id="element-564">
                    Capacity {selectedRoom.capacity} ·{' '}
                    {selectedRoom.features.slice(0, 2).join(' · ')}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 pb-6 flex gap-3" data-id="element-565">
              <button onClick={() => setShowBookingModal(false)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50" style={{
            borderColor: '#E4EAF6',
            color: '#5A6A8A'
          }} data-id="element-566">
                Cancel
              </button>
              <button onClick={handleConfirmBooking} disabled={!bookingForm.title.trim()} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 flex items-center justify-center gap-2" style={{
            background: bookingForm.title.trim() ? selectedRoom.color : '#CBD5E1',
            cursor: bookingForm.title.trim() ? 'pointer' : 'not-allowed'
          }} data-id="element-567">
                <CheckCircleIcon size={16} data-id="element-568" /> Confirm Booking
              </button>
            </div>
          </div>
        </div>}
    </div>;
}